/**
 * Query params that carry credentials and must never reach telemetry.
 *
 * The WebView sign-in token is stripped from the URL before RUM starts (see
 * `captureAuthTokenFromUrl`), so this is defence in depth for any path that
 * still manages to observe one — a referrer captured mid-navigation, a
 * resource request, a hand-built link.
 */
export const REDACTED_QUERY_PARAMS = ["token"];

/**
 * Replace the value of any credential-bearing query param with `REDACTED`,
 * leaving the rest of the URL intact. Returns the input unchanged if it is not
 * an absolute URL, so it is safe to apply to arbitrary telemetry fields.
 */
export default function redactUrl<T extends string | undefined>(url: T): T {
    if (!url) return url;
    try {
        const parsed = new URL(url);
        let redacted = false;
        REDACTED_QUERY_PARAMS.forEach((param) => {
            if (parsed.searchParams.has(param)) {
                parsed.searchParams.set(param, "REDACTED");
                redacted = true;
            }
        });
        return (redacted ? parsed.toString() : url) as T;
    } catch {
        // Not an absolute URL; nothing safe to rewrite.
        return url;
    }
}
